import { concat, flatten, uniq, cloneDeep } from "lodash"
import { addMinutes, getRoundHour, getTime } from "../../lib/utils"
import { DURATION_SLOT_MIN, ITEMS_PER_CAROUSEL_PAGE, PATH, PATH_DEEP, PREVENTION_PATH_DEEP, preventionDocumentLoader, preventionSurveyEnd, selected_documents } from "./models"
import { ACTION, Answer, OptionsSettings, Question, Reply, Survey, UiPosition, Value } from "./types"
import { pick } from "lodash"
import { cdn } from "core/cdn"
import config from "react-global-configuration"
import { Gender } from "types/entity"
/*
 *
 */
export const get_complete_path = (pathId: string): string[] => {
  const partial_path = pathId.split(":")

  let pathToIncrement = ""
  return partial_path.map((step: string, key: number) => {
    pathToIncrement += key ? `:${step}` : `${step}`
    return pathToIncrement
  })
}

export const replace_question = (
  questions: Question[],
  question: Question,
): Question[] => {
  return questions.map((_q) =>
    _q.id === (question as Question).id ? question : _q,
  ) as Question[]
}
export const is_deep_path = (path: string): boolean => path.includes(PATH_DEEP)

export const get_index_of_position = (
  position: UiPosition,
  itemsLength: number,
): number => {
  switch (position) {
    case "right":
      return itemsLength
    case "middle":
      return itemsLength / 2
    case "left":
    default:
      return 0
  }
}

export function split<T>(array: T[], nbElement: number): T[][] {
  const chunk = []
  while (array.length > 0) chunk.push(array.splice(0, nbElement))
  return chunk
}

export const splitCarouselPages = (
  items: Reply[],
  nbElement: number,
): Reply[][] => {
  const chunk = []
  const array = cloneDeep<Reply[]>(items)
  while (array.length > 0) chunk.push(array.splice(0, nbElement))
  return chunk
}
// @TODO => handle recursive get for bilan prevention ?
export const recursive_get = (question: Question, path: string[]): Question => {
  if (path.indexOf(question.id) === -1) return question
  else {
    path.splice(path.indexOf(question.id), 1)
    if (!path.length) return question
    else if (question.subquestion)
      return recursive_get(question.subquestion, path)
  }
  return question
}

/*
 * Cherche une question par son id : questions parentes, sous-questions
 * et enfants d'une question `questions_line`.
 */
export const findQuestionById = (
  questionId: string,
  questions: Question[],
): Question | undefined => {
  for (const question of questions) {
    if (question.id === questionId) return question
    if (question.subquestion) {
      const sub = findQuestionById(questionId, [question.subquestion])
      if (sub) return sub
    }
    const nested = (question as Question & { questions?: Question[] }).questions
    if (nested) {
      const found = findQuestionById(questionId, nested)
      if (found) return found
    }
  }
  return undefined
}

/*
 * Question `questions_line` qui porte la question passée en paramètre.
 * Sa réponse est stockée dans la réponse du parent : { title: childId, value }
 */
export const findQuestionsLineParent = (
  questionId: string,
  questions: Question[],
): (Question & { questions: Question[] }) | undefined =>
  questions.find(
    (question) =>
      question.ui?.view === "questions_line" &&
      (question as Question & { questions?: Question[] }).questions?.some(
        (child) => child.id === questionId,
      ),
  ) as (Question & { questions: Question[] }) | undefined

/*
 * from replies, give the new part of the _path
 */
export const get_sub_path = (replies: Reply[], current: Question): string[] =>
  replies
    .map(({ action }: Reply) =>
      action === ACTION.subquestion && current.subquestion
        ? current.subquestion?.id
        : undefined,
    )
    .filter((path: string | undefined) => path) as string[]

/*
 * update path question regarding the lasts replies
 */
export const update_path = (
  lastReplies: Reply[],
  actualPath: string[],
  current: Question,
): string[] => {
  const subPath: string[] = get_sub_path(lastReplies, current)

  const split_subPath = uniq(
    flatten(subPath.map((sub) => sub.split(PATH_DEEP))),
  )

  const path: string[] | string[][] =
    actualPath.map((step) => {
      const parentOfSub = split_subPath
        .filter((v, key) => key < split_subPath.length - 1)
        .join(PATH_DEEP)

      if (step === parentOfSub) {
        return [step, ...subPath]
      }
      return [step]
    }) || actualPath
  return uniq(flatten(path))
}

export const DocTypeToPicture = {
  prevention: cdn("images/pico_compte-rendu.svg"),
  report: cdn("images/pico_compte-rendu.svg"),
  prescription: cdn("images/pico_ordonnance.svg"),
  treatment: cdn("images/pico_medicament.svg"),
  administrative: cdn("images/pico_piece_administrative.svg"),
  picture: cdn("images/pico_photo_corporelle.svg"),
  other: cdn("images/pico_autre.svg"),
  certificate: cdn("images/pico_certificat.svg"),
  analyses: cdn("images/pico_analyse_bio.svg"),
  letter: cdn("images/pico_courrier_adressage.svg"),
  imaging: cdn("images/picto_radiologie.svg"),
  insurance: cdn("images/picto_insurance.svg"),
}

export const getUsefulData = (doc: any) => {
  const TIME_TO_PRESELECT_DOC = config.get("timeout.select_doc_time")
  const value = pick(doc, ["id", "created_at", "type", "title"])
  const then = new Date(value.created_at)
  const now = new Date()
  const month = then.getMonth() + 1
  const day = then.getDate()
  const year = then.getFullYear()
  const msBetweenDates = Math.abs(then.getTime() - now.getTime())
  const hoursBetweenDates = msBetweenDates / (60 * 60 * 1000)
  const preselect = hoursBetweenDates < TIME_TO_PRESELECT_DOC
  const reply = {
    imgUrl: DocTypeToPicture[value.type],
    title: `${value.title} ${day}/${month}/${year}`,
    value: value.id,
    preselect: preselect,
  }
  return reply
}

export const isThisDocNewlyUpdated = (doc: any, created_at: string | Date) => {
  const documentAge = new Date(doc.created_at).getTime()
  const linkAge = new Date(created_at).getTime()
  const createdAfterLinkRequest = linkAge < documentAge
  return createdAfterLinkRequest ? doc : null
}

export const update_answers = (answers: Answer[], fresh: Answer): Answer[] => {
  const a = answers
  const toSet: Answer | undefined = a.find(
    ({ questionId }) => questionId === fresh.questionId,
  )

  if (toSet !== undefined) {
    fresh.value = uniq(concat(toSet.value, fresh.value))
    a.splice(answers.indexOf(toSet), 1)
  }
  // Push fresh values
  a.push(fresh)
  return a
}

export function insertAt<T>(array: T[], index: number, ...elements: T[]): void {
  array.splice(index, 0, ...elements)
}

export const getSlotsReplies = (
  beforeSlots: number,
  afterSlots: number,
): Reply[] => {
  const date: Date = getRoundHour()
  const passedDate = addMinutes(
    date,
    ITEMS_PER_CAROUSEL_PAGE * beforeSlots * -DURATION_SLOT_MIN,
  )
  // CountSlots is the number of slots page
  // regarding the nb of items in a carousel page
  const countSlots: number =
    ITEMS_PER_CAROUSEL_PAGE * (afterSlots + beforeSlots)
  const replies: Reply[] = []
  for (let i = 0; i < countSlots; i++) {
    const minutesToAdd = i * DURATION_SLOT_MIN
    replies.push({
      title: getTime(addMinutes(passedDate, minutesToAdd)),
      value: addMinutes(passedDate, minutesToAdd).getTime(),
    })
  }
  return replies
}

export function isValueWithIdentifier(
  v: Value,
): v is { identifier: string; [key: string]: any } {
  return (
    v !== null &&
    typeof v === "object" &&
    !Array.isArray(v) &&
    "identifier" in v
  )
}

export function isValueSelected(selected: Value[], value: Value): boolean {
  return selected.some((s) => valueEquals(s, value))
}

export function valueEquals(a: Value, b: Value): boolean {
  if (isValueWithIdentifier(a) && isValueWithIdentifier(b)) {
    return a.identifier === b.identifier
  }
  return a === b
}

/*
* Transform a json survey from mobile application (prevention-report) to kiosk survey
*/

const getUiViewByType = (type: 'radiogroup' | 'checkbox' | 'dropdown' | 'textfield') => {
  switch(type) {
    case 'radiogroup':
      return "radiogroup";
    case 'checkbox':
      return "carousel";
    case 'dropdown':
      return "code";
    case "textfield":
      return "input";
    default:
      return "carousel";
  }
}

const getRepliesByType = (question) => {
  switch(question.type) {
    case 'dropdown':
      return [{
          unit: question.settings.unit,
          min: question.settings.min,
          max: question.settings.max?.toString().length,
          title: question?.settings?.unit === 'm' ? 'Taille' : 'Poids',
        },
      ];
    case 'textfield':
      return [{
          title: question.settings.placeholder,
        }]
    default:
    return (question.settings as OptionsSettings)?.options?.map((option) => ({
          value: option.id,
          title: option.label,
          gender: option.gender === "female" ? Gender.FEMALE : option.gender === "male" ? Gender.MALE : undefined,
          preventionAlert: option.alert,
        }))
  }
}

export const transformMobileSurveyToKiosk = (
  template: Survey,
  options?: { gender?: Gender },
) => {
  const questions: any[] = [];

  template?.content?.forEach((c, index) => {
    const questionIndex = index+1;
    c.questions.forEach((q) => {
      // "QX bis - " titles: legacy detection kept for templates not yet stripped.
      if(q.personalization?.bis || /^Q[0-9]*.(bis).-/.test(q.title)) {
        // inject has subquestion of last question
        questions[questions.length-1].subquestion = {
          title: q.title,
          label: q.label,
          sourceId: q.id,
          pdf: { type: q.type },
          preventionAlertRule: q.alert,
          id: `prevention-report-${questionIndex}${PREVENTION_PATH_DEEP}${q.id-1}${PATH_DEEP}${q.id}`,
          ui: { view: getUiViewByType(q.type) },
          multipleReplies: q.type === "checkbox",
          replies: getRepliesByType(q),
          allowSkip: q.type !== 'dropdown' && q.type !== 'textfield',
        }
        if(q.type === 'textfield') {
          questions[questions.length-1].replies.push({title: 'Autre', value: "autre", action: ACTION.subquestion})
        } else {
          questions[questions.length-1].replies.forEach((r) => r.action = ACTION.subquestion);
        }
        return;
      }
      const question: any = {
        title: q.title,
        label: q.label,
        subtitle: c.label,
        sectionId: c.id,
        sourceId: q.id,
        pdf: { type: q.type },
        preventionAlertRule: q.alert,
        id: `prevention-report-${questionIndex}${PREVENTION_PATH_DEEP}${q.id}`,
        multipleReplies: q.type === "checkbox",
        ui: { view: getUiViewByType(q.type) },
        replies: getRepliesByType(q),
        allowSkip: q.type !== 'dropdown',
        order: ['subtitle', 'title']
      }
      const personalization = q.personalization
      if (personalization) {
        if (personalization.themes?.length) question.themes = personalization.themes
        if (personalization.genderOnly)
          question.genderOnly =
            personalization.genderOnly === "female" ? Gender.FEMALE : Gender.MALE
        if (personalization.themeSelector) question.themeSelector = true
        if (personalization.mandatory) question.mandatory = true
        if (personalization.autofillGender) question.autofillGender = true
      }
      questions.push(question)
    })
  })

  personalizeQuestionsInPlace(questions, template, options?.gender)
  questions.push(preventionSurveyEnd)
  questions.push(preventionDocumentLoader)
  questions.push({...selected_documents, id: PATH.selected_documents_prevention});
  return questions;
}

// Personalize questions: removes irrelevant questions by theme, gender or conditional to previous questions
const personalizeQuestionsInPlace = (
  questions: any[],
  template: Survey,
  gender?: Gender,
): void => {
  // Resolve conditional metadata from each template question's personalization block.
  template?.content?.forEach((c) => {
    c.questions.forEach((q) => {
      const cond = q.personalization?.conditionalOn
      if (!cond) return
      const dependent = questions.find((kq) => kq.sourceId === q.id)
      const parent = questions.find((kq) => kq.sourceId === cond.parentId)
      if (!dependent || !parent) return
      dependent.conditionalOn = {
        questionId: parent.id,
        showIfValues: cond.showIfAnswers ?? [],
      }
    })
  })

  const genderKnown = gender === Gender.MALE || gender === Gender.FEMALE

  if (genderKnown) {
    // Keep questions for this gender (untagged = both). Subquestions ("bis")
    // ride their parent, so filtering top-level questions is enough.
    const isForGender = (q: any) => q.genderOnly === undefined || q.genderOnly === gender
    const kept = questions.filter(isForGender)
    questions.splice(0, questions.length, ...kept)
  }

  const selector = questions.filter((q) => q.themeSelector)
  const mandatory = questions.filter((q) => q.mandatory && !q.themeSelector)
  const rest = questions.filter((q) => !q.mandatory && !q.themeSelector)
  questions.splice(0, questions.length, ...selector, ...mandatory, ...rest)
}
